import { config } from '../config';
import { User } from '../../types/api';
import { tokenManager } from '../auth/tokenManager';

interface FriendRequest {
  friend_id: number;
  friend_full_name: string;
}

interface Friend {
  friend_id: number;
  friend_full_name: string;
  nickname?: string;
  birthday?: string | null;
  profile_picture?: number;
  wishlist?: Array<{
    id: number;
    name: string;
    price?: string;
    isPublic?: boolean;
  }>;
}

type FriendRequestStatus = 'pending' | 'accepted' | 'available';

interface FriendRequestStatusResponse {
  status: FriendRequestStatus;
}

export const friendsApi = {
  getFriendRequestStatus: async (userId: number, receiverId: number): Promise<FriendRequestStatus> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend-request/${receiverId}/status`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    const data: FriendRequestStatusResponse = await response.json();
    return data.status;
  },

  getFriends: async (userId: number): Promise<Friend[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friends`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getFriendProfile: async (userId: number, friendId: number): Promise<Friend> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend/${friendId}`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    const data = await response.json();
    return {
      friend_id: data.id,
      friend_full_name: data.full_name,
      nickname: data.nickname,
      birthday: data.birthday,
      profile_picture: data.profile_picture,
      wishlist: data.wishlist
    };
  },

  getPendingFriendRequests: async (userId: number): Promise<FriendRequest[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend-request/pending`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  sendFriendRequest: async (userId: number, friendId: number): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend-requests`, {
      method: 'POST',
      body: JSON.stringify({
        sender_id: userId,
        receiver_id: friendId
      }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  cancelFriendRequest: async (userId: number, friendId: number): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend-requests/${friendId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  respondToFriendRequest: async (userId: number, requestId: number, response: { status: 'accepted' | 'rejected' }): Promise<void> => {
    const apiResponse = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend-request/${requestId}`, {
      method: 'PATCH',
      body: JSON.stringify(response),
    });

    if (!apiResponse.ok) {
      const data = await apiResponse.json();
      throw new Error(data.detail || data.message || apiResponse.statusText);
    }
  },

  removeFriend: async (userId: number, friendId: number): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend/${friendId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },
};
